import { useMemo, useState } from 'react';
import { Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '@/components/ui/Modal';
import { callBackend, invalidateQueries } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { useCrm } from './CrmContext';
import { parseCsv } from './csv';
import { buttonClass, errorMessage, inputClass } from './format';
import { LIFECYCLE_LABELS, type Lifecycle } from './types';
import { canonicalPhone } from '../../../convex/crmModel';

type Match = { id: string; name: string; company?: string; kind: 'contact' | 'caller'; phones: string[]; phoneMatch: boolean };
type ReviewRow = { matches: Match[]; choice: 'import' | 'skip'; repeated: boolean };

const TARGETS = [
  { key: 'name', label: 'Name', guesses: ['name', 'full name', 'contact', 'contact name'] },
  { key: 'firstName', label: 'First name', guesses: ['first name', 'firstname', 'given name'] },
  { key: 'lastName', label: 'Last name', guesses: ['last name', 'lastname', 'surname', 'family name'] },
  { key: 'company', label: 'Company', guesses: ['company', 'organisation', 'organization', 'account'] },
  { key: 'jobTitle', label: 'Job title', guesses: ['job title', 'title', 'position', 'role'] },
  { key: 'email', label: 'Email', guesses: ['email', 'e-mail', 'email address'] },
  { key: 'mobile', label: 'Mobile', guesses: ['mobile', 'cell', 'cellphone', 'mobile phone'] },
  { key: 'office', label: 'Office phone', guesses: ['office', 'phone', 'telephone', 'work phone', 'business phone', 'tel'] },
  { key: 'tags', label: 'Tags', guesses: ['tags', 'labels', 'groups'] },
  { key: 'lifecycle', label: 'Stage', guesses: ['stage', 'lifecycle', 'status', 'type'] },
  { key: 'notes', label: 'Notes', guesses: ['notes', 'note', 'remark', 'comments'] },
] as const;
type TargetKey = (typeof TARGETS)[number]['key'];

function guessMapping(header: string[]) {
  const mapping: Partial<Record<TargetKey, number>> = {};
  const used = new Set<number>();
  for (const target of TARGETS) {
    const index = header.findIndex((column, i) => !used.has(i) && (target.guesses as readonly string[]).includes(column.trim().toLowerCase()));
    if (index >= 0) {
      mapping[target.key] = index;
      used.add(index);
    }
  }
  return mapping;
}

function toLifecycle(value: string): Lifecycle | undefined {
  const lower = value.trim().toLowerCase();
  return (Object.keys(LIFECYCLE_LABELS) as Lifecycle[]).find((key) => key === lower || LIFECYCLE_LABELS[key].toLowerCase() === lower);
}

export function CsvImport({ onClose }: { onClose: () => void }) {
  const { data } = useCrm();
  const [rows, setRows] = useState<string[][] | null>(null);
  const [mapping, setMapping] = useState<Partial<Record<TargetKey, number>>>({});
  const [progress, setProgress] = useState<{ done: number; total: number; created: number; updated: number; failed: string[] } | null>(null);
  const [review, setReview] = useState<ReviewRow[] | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const header = rows?.[0] ?? [];
  const body = useMemo(() => rows?.slice(1) ?? [], [rows]);

  const records = useMemo(() => {
    const get = (row: string[], key: TargetKey) => (mapping[key] !== undefined ? (row[mapping[key]!] ?? '').trim() : '');
    return body.map((row) => {
      const name = get(row, 'name') || [get(row, 'firstName'), get(row, 'lastName')].filter(Boolean).join(' ');
      return {
        name,
        company: get(row, 'company') || undefined,
        jobTitle: get(row, 'jobTitle') || undefined,
        email: get(row, 'email') || undefined,
        mobile: get(row, 'mobile') || undefined,
        office: get(row, 'office') || undefined,
        tags: get(row, 'tags') ? get(row, 'tags').split(/[;,|]/).map((tag) => tag.trim()).filter(Boolean) : undefined,
        lifecycle: toLifecycle(get(row, 'lifecycle')),
        notes: get(row, 'notes') || undefined,
      };
    });
  }, [body, mapping]);
  const valid = records.filter((record) => record.name && (record.mobile || record.office));

  const readFile = async (file: File) => {
    const parsed = parseCsv(await file.text());
    if (parsed.length < 2) {
      toast.error('That file has no rows to import');
      return;
    }
    setRows(parsed);
    setMapping(guessMapping(parsed[0]));
  };

  const prepare = async () => {
    setReviewing(true);
    try {
      const found: Match[][] = [];
      for (let index = 0; index < valid.length; index += 100) {
        const batch = valid.slice(index, index + 100);
        found.push(...await callBackend<Match[][]>(backendApi.crm.previewContactMatches, { rows: batch.map((row) => ({ name: row.name, email: row.email, numbers: [row.mobile, row.office].filter(Boolean) })) }));
      }
      const seen = new Set<string>();
      setReview(valid.map((row, index) => {
        const numbers = [row.mobile, row.office].filter((number): number is string => Boolean(number)).map(canonicalPhone).filter(Boolean);
        const repeated = numbers.some((number) => seen.has(number));
        numbers.forEach((number) => seen.add(number));
        return { matches: found[index], repeated, choice: found[index].length || repeated ? 'skip' : 'import' };
      }));
    } catch (error) { toast.error(errorMessage(error)); }
    finally { setReviewing(false); }
  };

  const run = async () => {
    if (!review) return;
    const selected = valid.flatMap((record, index) => review[index].choice === 'import' ? [{ ...record, expectedContactId: review[index].matches.find((match) => match.phoneMatch)?.id }] : []);
    const state = { done: 0, total: selected.length, created: 0, updated: 0, failed: [] as string[] };
    setProgress({ ...state });
    try {
      for (let index = 0; index < selected.length; index += 200) {
        const chunk = selected.slice(index, index + 200);
        const results = await callBackend<Array<{ row: number; ok: boolean; action?: 'created' | 'updated'; error?: string }>>(backendApi.crm.importContacts, { rows: chunk });
        for (const result of results) {
          if (result.ok && result.action === 'created') state.created += 1;
          else if (result.ok) state.updated += 1;
          else state.failed.push(`${chunk[result.row]?.name}: ${result.error}`);
        }
        state.done += chunk.length;
        setProgress({ ...state });
      }
      invalidateQueries();
      toast.success(`Imported: ${state.created} new, ${state.updated} updated`);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Import contacts from CSV" size="lg">
      {!rows ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Upload a CSV export from Excel, Outlook, Google Contacts or another CRM. Each row needs a name and a phone number. You can review matching records before importing.
            {data?.features.phonebookSync ? ' Imported contacts are also written to the PBX phone book.' : ''}
          </p>
          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-10 cursor-pointer hover:border-primary-400">
            <Upload className="w-8 h-8 text-gray-400" />
            <span className="text-sm text-gray-600 dark:text-gray-300">Choose a .csv file</span>
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={(event) => event.target.files?.[0] && void readFile(event.target.files[0])} />
          </label>
        </div>
      ) : progress ? (
        <div className="space-y-3 text-sm">
          <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div className="h-full bg-primary-600 transition-all" style={{ width: `${(progress.done / Math.max(1, progress.total)) * 100}%` }} />
          </div>
          <p className="text-gray-700 dark:text-gray-200">
            {progress.done} of {progress.total} · {progress.created} new · {progress.updated} updated · {progress.failed.length} failed
          </p>
          {progress.failed.length > 0 && (
            <ul className="max-h-40 overflow-y-auto text-xs text-red-600 space-y-0.5">
              {progress.failed.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          {progress.done === progress.total && (
            <button type="button" onClick={onClose} className={buttonClass.primary}>
              Close
            </button>
          )}
        </div>
      ) : review ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">Review every match. Existing records are skipped until you choose to update them. Rows with multiple matches or repeated numbers in the file must be resolved separately.</p>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {valid.map((row, index) => {
              const item = review[index];
              const phoneMatches = item.matches.filter((match) => match.phoneMatch);
              const conflict = item.repeated || phoneMatches.length > 1;
              return <div key={index} className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 text-sm">
                <p className="font-medium">{row.name} · {[row.mobile, row.office].filter(Boolean).join(', ')}</p>
                <p className="text-xs text-gray-500">{item.repeated ? 'Number repeated in this file. ' : ''}{item.matches.length ? `Possible matches: ${item.matches.map((match) => `${match.name}${match.company ? ` (${match.company})` : ''} [${match.phoneMatch ? 'same number' : 'name or email'}]`).join(', ')}` : 'No matches found'}</p>
                <label className="text-xs"><input type="checkbox" disabled={conflict} checked={item.choice === 'import'} onChange={(event) => setReview((current) => current!.map((entry, i) => i === index ? { ...entry, choice: event.target.checked ? 'import' : 'skip' } : entry))} /> {phoneMatches.length === 1 ? `Update ${phoneMatches[0].name}` : 'Import as new'}</label>
              </div>;
            })}
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setReview(null)} className={buttonClass.secondary}>Back to mapping</button>
            <button type="button" disabled={!review.some((row) => row.choice === 'import')} onClick={() => void run()} className={buttonClass.primary}>Import {review.filter((row) => row.choice === 'import').length}</button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">Match your columns. {body.length} rows found; {valid.length} have a name and a number.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TARGETS.map((target) => (
              <label key={target.key} className="text-sm">
                <span className="block text-gray-700 dark:text-gray-300 mb-1">{target.label}</span>
                <select
                  value={mapping[target.key] ?? ''}
                  onChange={(event) => setMapping({ ...mapping, [target.key]: event.target.value === '' ? undefined : Number(event.target.value) })}
                  className={inputClass}
                >
                  <option value="">—</option>
                  {header.map((column, index) => (
                    <option key={index} value={index}>
                      {column || `Column ${index + 1}`}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          {valid.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-gray-500">
                  <tr>
                    <th className="py-1 pr-2">Name</th>
                    <th className="py-1 pr-2">Company</th>
                    <th className="py-1 pr-2">Mobile</th>
                    <th className="py-1 pr-2">Office</th>
                    <th className="py-1">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {valid.slice(0, 5).map((record, index) => (
                    <tr key={index} className="text-gray-800 dark:text-gray-200">
                      <td className="py-1 pr-2">{record.name}</td>
                      <td className="py-1 pr-2">{record.company}</td>
                      <td className="py-1 pr-2">{record.mobile}</td>
                      <td className="py-1 pr-2">{record.office}</td>
                      <td className="py-1">{record.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setRows(null)} className={buttonClass.secondary}>
              Another file
            </button>
            <button type="button" onClick={() => void prepare()} disabled={valid.length === 0 || reviewing} className={buttonClass.primary}>
              {reviewing ? 'Checking…' : `Review ${valid.length}`}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
